// Table utilities: CSV/Excel/PDF export + sortable header hook.
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";

export function downloadCSV(rows, filename = "export.csv") {
  const csv = rows.map(r =>
    r.map(c => `"${(c ?? "").toString().replace(/"/g, '""')}"`).join(",")
  ).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export function downloadXLSX(rows, filename = "export.xlsx", sheetName = "Sheet1") {
  const ws = XLSX.utils.aoa_to_sheet(rows);
  // Auto-width columns
  const widths = rows[0].map((_, ci) => {
    const max = Math.max(...rows.map(r => String(r[ci] ?? "").length));
    return { wch: Math.min(Math.max(max + 2, 10), 50) };
  });
  ws["!cols"] = widths;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  XLSX.writeFile(wb, filename);
}

// Simple tabular PDF (landscape A4). Header + rows with automatic pagination.
export function downloadTablePDF(rows, filename = "export.pdf", opts = {}) {
  const title = opts.title || "KELTA Export";
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape" });
  const pageW = 297, pageH = 210;
  // KELTA tricolor header band
  doc.setFillColor(255, 153, 51);   // saffron
  doc.rect(0, 0, pageW, 2.5, "F");
  doc.setFillColor(255, 255, 255);
  doc.rect(0, 2.5, pageW, 1.5, "F");
  doc.setFillColor(19, 136, 8);     // green
  doc.rect(0, 4, pageW, 2.5, "F");
  doc.setFillColor(0, 94, 184);     // KELTA blue hairline
  doc.rect(0, 6.5, pageW, 0.7, "F");

  doc.setTextColor(0, 94, 184);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.text(title, 12, 14);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(110, 118, 132);
  doc.text(new Date().toLocaleString("en-IN"), pageW - 12, 14, { align: "right" });

  const header = rows[0];
  const body = rows.slice(1);
  const cols = header.length;
  const marginX = 10;
  const availW = pageW - marginX * 2;
  const colW = availW / cols;

  const drawHeaderRow = (y) => {
    doc.setFillColor(0, 94, 184);
    doc.rect(marginX, y, availW, 7, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    header.forEach((h, i) => {
      doc.text(String(h), marginX + i * colW + 2, y + 5);
    });
  };

  let y = 22;
  drawHeaderRow(y);
  y += 7;
  doc.setFont("helvetica", "normal");
  doc.setTextColor(30, 30, 35);
  doc.setFontSize(8);
  const rowH = 6.5;

  body.forEach((row, ri) => {
    if (y + rowH > pageH - 12) {
      // page footer
      doc.setDrawColor(220, 224, 232);
      doc.line(marginX, pageH - 10, pageW - marginX, pageH - 10);
      doc.setFontSize(7);
      doc.setTextColor(110, 118, 132);
      doc.text("KELTA — Kerala Local Body Technical Assistants Association", marginX, pageH - 6);
      doc.text(`Page ${doc.getCurrentPageInfo().pageNumber}`, pageW - marginX, pageH - 6, { align: "right" });
      doc.addPage();
      // repeat header on new page
      doc.setFillColor(255, 153, 51);
      doc.rect(0, 0, pageW, 2.5, "F");
      doc.setFillColor(255, 255, 255);
      doc.rect(0, 2.5, pageW, 1.5, "F");
      doc.setFillColor(19, 136, 8);
      doc.rect(0, 4, pageW, 2.5, "F");
      doc.setFillColor(0, 94, 184);
      doc.rect(0, 6.5, pageW, 0.7, "F");
      y = 15;
      drawHeaderRow(y);
      y += 7;
      doc.setFont("helvetica", "normal");
      doc.setTextColor(30, 30, 35);
      doc.setFontSize(8);
    }
    if (ri % 2 === 1) {
      doc.setFillColor(248, 249, 251);
      doc.rect(marginX, y, availW, rowH, "F");
    }
    row.forEach((c, i) => {
      const str = String(c ?? "");
      const maxChars = Math.floor((colW - 3) / 1.6);
      const clipped = str.length > maxChars ? str.slice(0, maxChars - 1) + "…" : str;
      doc.text(clipped, marginX + i * colW + 2, y + 4.5);
    });
    y += rowH;
  });

  // final footer
  doc.setDrawColor(220, 224, 232);
  doc.line(marginX, pageH - 10, pageW - marginX, pageH - 10);
  doc.setFontSize(7);
  doc.setTextColor(110, 118, 132);
  doc.text("KELTA — Kerala Local Body Technical Assistants Association", marginX, pageH - 6);
  doc.text(`Page ${doc.getCurrentPageInfo().pageNumber}`, pageW - marginX, pageH - 6, { align: "right" });
  doc.save(filename);
}

// Sort helper: pass an array, sortState={key, dir: 'asc'|'desc'} and a keyMap {colKey: (row)=>value}.
export function sortRows(rows, sortState, keyMap) {
  if (!sortState || !sortState.key) return rows;
  const { key, dir } = sortState;
  const get = keyMap[key] || ((r) => r[key]);
  const copy = [...rows];
  copy.sort((a, b) => {
    const va = get(a), vb = get(b);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    if (typeof va === "number" && typeof vb === "number") return dir === "asc" ? va - vb : vb - va;
    const sa = String(va).toLowerCase(), sb = String(vb).toLowerCase();
    if (sa < sb) return dir === "asc" ? -1 : 1;
    if (sa > sb) return dir === "asc" ? 1 : -1;
    return 0;
  });
  return copy;
}

export function toggleSort(prev, key) {
  if (!prev || prev.key !== key) return { key, dir: "asc" };
  if (prev.dir === "asc") return { key, dir: "desc" };
  return { key: null, dir: null };
}
